import React from 'react';
import {
  Box,
  Button,
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ButtonGroup,
  useTheme,
  useMediaQuery,
  Collapse,
  Autocomplete,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { ReviewStatus, ValidationQuality, TrainingSampleFilterInput, Disease } from '../../types';

interface TrainingSampleFiltersProps {
  filter: TrainingSampleFilterInput;
  dateFilter: string;
  showFilters: boolean;
  diseases: Disease[];
  diseaseLoading?: boolean;
  sortDirection: 'ASC' | 'DESC';
  onFilterChange: (filter: TrainingSampleFilterInput) => void;
  onDateFilterChange: (period: string) => void;
  onToggleFilters: () => void;
  onApplyFilter: () => void;
  onClearFilters: () => void;
  onDiseaseSearch?: (input: string) => void;
  onDiseaseSelect?: (disease: Disease | null) => void;
  onSortDirectionChange: (direction: 'ASC' | 'DESC') => void;
}

export const TrainingSampleFilters: React.FC<TrainingSampleFiltersProps> = ({
  filter,
  dateFilter,
  showFilters,
  diseases,
  diseaseLoading = false,
  sortDirection,
  onFilterChange,
  onDateFilterChange,
  onToggleFilters,
  onApplyFilter,
  onClearFilters,
  onDiseaseSearch,
  onDiseaseSelect,
  onSortDirectionChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const selectedDisease = diseases.find(d => d.id === filter.label_id) || null;

  return (
    <>
      {/* Date Filter Buttons */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
          width: { xs: '100%', sm: 'auto' },
        }}>
          {isMobile ? (
            // Mobile: Vertical stack of buttons
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant={dateFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => onDateFilterChange('all')}
                  size="small"
                >
                  All
                </Button>
                <Button
                  fullWidth
                  variant={dateFilter === 'today' ? 'contained' : 'outlined'}
                  onClick={() => onDateFilterChange('today')}
                  size="small"
                >
                  Today
                </Button>
                <Button
                  fullWidth
                  variant={dateFilter === 'week' ? 'contained' : 'outlined'}
                  onClick={() => onDateFilterChange('week')}
                  size="small"
                >
                  Week
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant={dateFilter === 'month' ? 'contained' : 'outlined'}
                  onClick={() => onDateFilterChange('month')}
                  size="small"
                >
                  Month
                </Button>
                <Button
                  fullWidth
                  variant={dateFilter === '3months' ? 'contained' : 'outlined'}
                  onClick={() => onDateFilterChange('3months')}
                  size="small"
                >
                  3 Months
                </Button>
              </Box>
            </Box>
          ) : (
            // Desktop/Tablet: Button group
            <ButtonGroup variant="outlined" size={isTablet ? 'small' : 'medium'}>
              <Button
                variant={dateFilter === 'all' ? 'contained' : 'outlined'}
                onClick={() => onDateFilterChange('all')}
              >
                All
              </Button>
              <Button
                variant={dateFilter === 'today' ? 'contained' : 'outlined'}
                onClick={() => onDateFilterChange('today')}
              >
                Today
              </Button>
              <Button
                variant={dateFilter === 'week' ? 'contained' : 'outlined'}
                onClick={() => onDateFilterChange('week')}
              >
                Week
              </Button>
              <Button
                variant={dateFilter === 'month' ? 'contained' : 'outlined'}
                onClick={() => onDateFilterChange('month')}
              >
                Month
              </Button>
              <Button
                variant={dateFilter === '3months' ? 'contained' : 'outlined'}
                onClick={() => onDateFilterChange('3months')}
              >
                3 Months
              </Button>
            </ButtonGroup>
          )}
        </Box>

        <FormControl size={isTablet ? 'small' : 'medium'} sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <InputLabel>Sort by Date</InputLabel>
          <Select
            label="Sort by Date"
            value={sortDirection}
            onChange={(event) => onSortDirectionChange(event.target.value as 'ASC' | 'DESC')}
          >
            <MenuItem value="DESC">Newest first</MenuItem>
            <MenuItem value="ASC">Oldest first</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={onToggleFilters}
          size={isMobile ? 'small' : 'medium'}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          More Filters
        </Button>
      </Box>

      {/* Advanced Filters */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={diseases}
                loading={diseaseLoading}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option.name}
                value={selectedDisease}
                onChange={(_, newValue) => {
                  onFilterChange({ ...filter, label_id: newValue?.id || undefined });
                  onDiseaseSelect?.(newValue || null);
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    onDiseaseSearch?.(newInputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Class/Disease" placeholder="Search classes..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Review Status</InputLabel>
                <Select
                  value={filter.review_status || ''}
                  label="Review Status"
                  onChange={(e) => onFilterChange({ ...filter, review_status: e.target.value as ReviewStatus })}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.values(ReviewStatus).map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Validation Quality</InputLabel>
                <Select
                  value={filter.validation_quality || ''}
                  label="Validation Quality"
                  onChange={(e) => onFilterChange({ ...filter, validation_quality: e.target.value as ValidationQuality })}
                >
                  <MenuItem value="">All</MenuItem>
                  {Object.values(ValidationQuality).map((quality) => (
                    <MenuItem key={quality} value={quality}>{quality}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Training Category"
                value={filter.training_category || ''}
                onChange={(e) => onFilterChange({ ...filter, training_category: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                justifyContent: 'flex-end'
              }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={onApplyFilter}
                  fullWidth={isMobile}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onClearFilters}
                  fullWidth={isMobile}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </Collapse>
    </>
  );
};